import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Search,
  ChevronDown,
  Play,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { debounce } from '../common/helpers';
import SortIcon from '../common/SortIcon';

export default function GenericExecuteQueryView({
  queries,
  contentType,
  executionUrlSuffix,
}) {
  const [selectedQueryId, setSelectedQueryId] = useState('');
  const [data, setData] = useState({ rows: [], page: 1, limit: 10, total: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [error, setError] = useState(null);
  const [activeQueryId, setActiveQueryId] = useState(null);
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');

  const totalPages = Math.ceil(data.total / data.limit);
  const debouncedFetch = useRef(
    debounce((...args) => fetchData(...args), 500)
  ).current;

  const fetchData = useCallback(
    async (
      page = 1,
      queryId = null,
      currentSortField = sortField,
      currentSortDirection = sortDirection,
      currentSearchTerm = searchTerm
    ) => {
      if (!isExecuting) setIsLoading(true);
      setError(null);
      if (queryId !== undefined) setActiveQueryId(queryId);
      const effectiveQueryId = queryId === null ? activeQueryId : queryId;

      try {
        const requestBodyData = { page, content_type: contentType };
        if (effectiveQueryId) requestBodyData.query_id = effectiveQueryId;
        if (currentSortField) {
          requestBodyData.order_by = currentSortField;
          requestBodyData.order_dir = currentSortDirection.toUpperCase();
        }
        if (currentSearchTerm && currentSearchTerm.trim() !== '') {
          requestBodyData.q = currentSearchTerm.trim();
        }

        const response = await fetch(
          'https://4e352fnqs7.execute-api.eu-central-1.amazonaws.com/uatt/do-action',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: 'Bearer 980c9cca-8fc2-11f0-8d33-02e13bb30071',
            },
            body: JSON.stringify({
              do_action: 'f_list_contents',
              data: requestBodyData,
            }),
          }
        );
        if (!response.ok) throw new Error(`API Error: ${response.status}`);
        const result = await response.json();
        setData(result);
      } catch (err) {
        setError(err.message);
        setData({ rows: [], page: 1, limit: 10, total: 0 });
      } finally {
        setIsLoading(false);
      }
    },
    [
      activeQueryId,
      isExecuting,
      sortField,
      sortDirection,
      searchTerm,
      contentType,
    ]
  );

  useEffect(() => {
    debouncedFetch(1, activeQueryId, sortField, sortDirection, searchTerm);
  }, [searchTerm, debouncedFetch, activeQueryId, sortField, sortDirection]);

  const handleExecuteClick = async () => {
    if (!selectedQueryId) return;
    setIsExecuting(true);
    setError(null);
    try {
      const response = await fetch(
        `https://odlz4j8wo6.execute-api.eu-central-1.amazonaws.com/uat/${executionUrlSuffix}?content_query_id=${selectedQueryId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer 980c9cca-8fc2-11f0-8d33-02e13bb30071',
          },
        }
      );
      if (!response.ok) throw new Error(`Execution API Error: ${response.status}`);
      await fetchData(1, selectedQueryId, sortField, sortDirection, searchTerm);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsExecuting(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchData(newPage, activeQueryId, sortField, sortDirection, searchTerm);
    }
  };

  const handleQuerySelection = (queryId) => {
    setSelectedQueryId(queryId);
    setSearchTerm('');
    fetchData(1, queryId, sortField, sortDirection, '');
  };

  const handleSort = (field) => {
    let newDirection = 'asc';
    if (sortField === field) newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);
    fetchData(1, activeQueryId, field, newDirection, searchTerm);
  };

  const handleSearchChange = (event) => setSearchTerm(event.target.value);

  const sortOptions = useMemo(
    () => ['title', 'created_at', 'published_at', 'priority_date'],
    []
  );

  return (
    <div>
      <div className="flex flex-col md:flex-row gap-4 mb-4">
        <div className="relative flex-grow">
          <label className="block text-sm font-medium text-gray-400 mb-2">
            Select Query
          </label>
          <select
            value={selectedQueryId}
            onChange={(e) => handleQuerySelection(e.target.value)}
            className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-4 appearance-none focus:outline-none focus:ring-2 focus:ring-cyan-500 transition duration-200 h-[42px]"
          >
            <option value="" disabled>
              Select a query to view/execute...
            </option>
            {queries.map((q) => (
              <option key={q.id} value={q.id}>
                {q.title}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 bottom-2.5 w-5 h-5 text-gray-400 pointer-events-none" />
        </div>
        <div className="flex items-end">
          <button
            onClick={handleExecuteClick}
            disabled={isLoading || isExecuting || !selectedQueryId}
            className="bg-cyan-600 hover:bg-cyan-500 disabled:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg flex items-center justify-center transition-all h-[42px]"
          >
            {isExecuting ? <Loader2 className="animate-spin" /> : <Play />}
          </button>
        </div>
      </div>
      <div className="mb-4">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            className="block w-full pl-10 pr-3 py-2 border border-gray-600 rounded-md leading-5 bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
            placeholder="Search by title or description..."
            type="search"
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </div>
      </div>
      <div className="mt-8 flex-grow flex flex-col min-h-[400px]">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-gray-300">Fetched Data</h3>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-gray-400">Sort by:</span>
            {sortOptions.map((field) => (
              <button
                key={field}
                onClick={() => handleSort(field)}
                className={`px-2 py-1 rounded text-sm ${
                  sortField === field
                    ? 'bg-cyan-600 text-white'
                    : 'bg-gray-600 hover:bg-gray-500'
                }`}
              >
                {field
                  .replace('_', ' ')
                  .replace(/\b\w/g, (l) => l.toUpperCase())}{' '}
                <SortIcon field={field} currentField={sortField} direction={sortDirection} />
              </button>
            ))}
            <button
              onClick={() =>
                fetchData(data.page, activeQueryId, sortField, sortDirection, searchTerm)
              }
              disabled={isLoading || isExecuting}
              className="bg-gray-600 hover:bg-gray-500 disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-semibold py-1 px-3 rounded-lg flex items-center gap-2 text-sm"
            >
              <RefreshCw
                size={14}
                className={isLoading || isExecuting ? 'animate-spin' : ''}
              />{' '}
              Refresh
            </button>
          </div>
        </div>
        <div className="bg-gray-900/50 p-4 rounded-lg flex-grow overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-12 h-12 text-cyan-400 animate-spin" />
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full text-red-400">
              <p>Error: {error}</p>
            </div>
          ) : data.rows && data.rows.length > 0 ? (
            <ul className="divide-y divide-gray-700">
              {data.rows.map((item, index) => (
                <li key={item.id || `${item.title}-${index}`} className="py-3">
                  {item.url ? (
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold text-cyan-400 hover:text-cyan-300 hover:underline"
                    >
                      {item.title || 'No Title'}
                    </a>
                  ) : (
                    <p className="font-semibold text-white">
                      {item.title || 'No Title'}
                    </p>
                  )}
                  <p className="text-sm text-gray-400 mt-1">
                    {item.description || 'No description available.'}
                  </p>
                  <div className="text-xs text-gray-500 mt-2 flex gap-4 flex-wrap">
                    {item.query_title && <span>Query: {item.query_title}</span>}
                    {item.created_at && (
                      <span>Pulled On: {new Date(item.created_at).toLocaleDateString()}</span>
                    )}
                    {item.priority_date && (
                      <span>Created: {new Date(item.priority_date).toLocaleString()}</span>
                    )}
                    {item.published_at && (
                      <span>Published: {new Date(item.published_at).toLocaleString()}</span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-center text-gray-500 flex items-center justify-center h-full">
              <p>No results found.</p>
            </div>
          )}
        </div>
        {!isLoading && data.total > 0 && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700">
            <button
              onClick={() => handlePageChange(data.page - 1)}
              disabled={data.page <= 1}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-500 disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-semibold rounded-lg"
            >
              Previous
            </button>
            <span className="text-gray-400">
              Page {data.page} of {totalPages}
            </span>
            <button
              onClick={() => handlePageChange(data.page + 1)}
              disabled={data.page >= totalPages}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-500 disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-semibold rounded-lg"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

